import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { getCountryName } from '@mahawi/eshop-common/src/get-country-name';
import { getFlag } from '@mahawi/eshop-common/src/get-flag';
import { numberCompare } from '@mahawi/eshop-common/src/number-compare';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  type IOssCountryRow,
  type IOssReport,
} from '@mahawi/eshop-common/src/types';
import {
  Alert,
  Button,
  Space,
  Statistic,
  Table as AntdTable,
  Typography,
} from 'antd';
import { type ColumnGroupType, type ColumnType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import DateRangeFilter from '../components/date-range-filter';
import DetailPageHeader from '../components/detail-page-header';
import Table from '../components/table';
import { EDateFormat } from '../fragments/date-format';
import {
  EDateRangePreset,
  formatDateRange,
  fromIsoDateRange,
  getPreviousDateRange,
  getQuarterLabel,
  type IDateRange,
  resolveDateRange,
  toInclusiveDates,
  toIsoDateRange,
} from '../fragments/date-range';
import {
  getOssFilingDeadline,
  getQuarterForecast,
  isCurrentQuarter,
  OSS_DATE_RANGE_PRESETS,
} from '../fragments/oss-period';
import { renderUpdatedAtMessage } from '../fragments/updated-at-message';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { reportOssLoad } from '../reducers/report/reducer';
import { COLOR, SPACING } from '../theme/tokens';

const { Text } = Typography;

const EUR_SUFFIX = 'EUR';

type TOssRow = IOssCountryRow & { key: string };

const formatEur = (value: number): string =>
  `${value.toFixed(2)} ${EUR_SUFFIX}`;

const getCountryLabel = (countryTypeIsoCode: string): string =>
  `${getFlag({ countryTypeIsoCode })} ${getCountryName({ countryTypeIsoCode })} (${countryTypeIsoCode})`;

const getDeltaColor = (delta: number): string =>
  delta > 0 ? COLOR.error : COLOR.success;

function ReportOssContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const Report = useSelector((state: RootState) => state.Report);

  const [range, setRange] = useState<IDateRange>(
    resolveDateRange(EDateRangePreset.THIS_QUARTER),
  );

  const handleLoad = (): void => {
    dispatch(
      reportOssLoad(
        toIsoDateRange(range),
        toIsoDateRange(getPreviousDateRange(range)),
      ),
    );
  };

  const report: IOssReport | undefined = Report.oss;
  const previousReport: IOssReport | undefined = Report.ossPrevious;

  const loadedPeriod: IDateRange | undefined = useMemo(
    () => (report ? fromIsoDateRange(report) : undefined),
    [report],
  );

  const previousPeriod: IDateRange | undefined = useMemo(
    () => (previousReport ? fromIsoDateRange(previousReport) : undefined),
    [previousReport],
  );

  const forecast: number | null = useMemo(() => {
    const now: Dayjs = dayjs();

    if (!report || !loadedPeriod || !isCurrentQuarter(loadedPeriod, now)) {
      return null;
    }

    return getQuarterForecast(report.totalNetVatEur, loadedPeriod, now);
  }, [report, loadedPeriod]);

  const dataSource: TOssRow[] = useMemo(
    () =>
      (report?.countries ?? []).map((row: IOssCountryRow): TOssRow => ({
        ...row,
        key: `${row.countryIsoCode}-${row.vatRatePct}`,
      })),
    [report],
  );

  const exportCsv = useCallback((): void => {
    if (!report || !loadedPeriod) {
      return;
    }

    const headers = [
      'Country',
      'VAT rate %',
      'Orders',
      'Tax base EUR',
      'VAT EUR',
      'Refund tax base EUR',
      'Refund VAT EUR',
      'Net VAT EUR',
    ];

    const rows = report.countries.map((row: IOssCountryRow): string =>
      [
        row.countryIsoCode,
        row.vatRatePct,
        row.orderCount,
        row.baseEur.toFixed(2),
        row.vatEur.toFixed(2),
        row.refundBaseEur.toFixed(2),
        row.refundVatEur.toFixed(2),
        row.netVatEur.toFixed(2),
      ].join(','),
    );

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `oss-vat-report-${toInclusiveDates(loadedPeriod).join('-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [report, loadedPeriod]);

  const columns = useMemo(
    () => [
      {
        title: 'Country',
        dataIndex: 'countryIsoCode',
        key: 'countryIsoCode',
        sorter: (a: TOssRow, b: TOssRow): number =>
          stringCompare(a.countryIsoCode, b.countryIsoCode),
        render: (value: string): string => getCountryLabel(value),
      },
      {
        title: 'VAT rate',
        dataIndex: 'vatRatePct',
        key: 'vatRatePct',
        align: 'right' as const,
        sorter: (a: TOssRow, b: TOssRow): number =>
          numberCompare(a.vatRatePct, b.vatRatePct),
        render: (value: number): string => `${value} %`,
      },
      {
        title: 'Orders',
        dataIndex: 'orderCount',
        key: 'orderCount',
        align: 'right' as const,
        sorter: (a: TOssRow, b: TOssRow): number =>
          numberCompare(a.orderCount, b.orderCount),
      },
      {
        title: 'Tax base',
        dataIndex: 'baseEur',
        key: 'baseEur',
        align: 'right' as const,
        sorter: (a: TOssRow, b: TOssRow): number =>
          numberCompare(a.baseEur, b.baseEur),
        render: formatEur,
      },
      {
        title: 'VAT',
        dataIndex: 'vatEur',
        key: 'vatEur',
        align: 'right' as const,
        sorter: (a: TOssRow, b: TOssRow): number =>
          numberCompare(a.vatEur, b.vatEur),
        render: formatEur,
      },
      {
        title: 'Refund tax base',
        dataIndex: 'refundBaseEur',
        key: 'refundBaseEur',
        align: 'right' as const,
        sorter: (a: TOssRow, b: TOssRow): number =>
          numberCompare(a.refundBaseEur, b.refundBaseEur),
        render: formatEur,
      },
      {
        title: 'Refund VAT',
        dataIndex: 'refundVatEur',
        key: 'refundVatEur',
        align: 'right' as const,
        sorter: (a: TOssRow, b: TOssRow): number =>
          numberCompare(a.refundVatEur, b.refundVatEur),
        render: formatEur,
      },
      {
        title: 'Net VAT',
        dataIndex: 'netVatEur',
        key: 'netVatEur',
        align: 'right' as const,
        defaultSortOrder: 'descend' as const,
        sorter: (a: TOssRow, b: TOssRow): number =>
          numberCompare(a.netVatEur, b.netVatEur),
        render: (value: number): React.ReactElement => (
          <Text strong>{formatEur(value)}</Text>
        ),
      },
    ],
    [],
  );

  const renderSummary = useCallback(
    (): React.ReactNode =>
      report ? (
        <AntdTable.Summary.Row>
          <AntdTable.Summary.Cell index={0} colSpan={2}>
            <Text strong>Total</Text>
          </AntdTable.Summary.Cell>
          <AntdTable.Summary.Cell index={2} align="right">
            {report.countries.reduce(
              (total: number, row: IOssCountryRow): number =>
                total + row.orderCount,
              0,
            )}
          </AntdTable.Summary.Cell>
          <AntdTable.Summary.Cell index={3} align="right">
            {formatEur(report.totalBaseEur)}
          </AntdTable.Summary.Cell>
          <AntdTable.Summary.Cell index={4} align="right">
            {formatEur(report.totalVatEur)}
          </AntdTable.Summary.Cell>
          <AntdTable.Summary.Cell index={5} align="right" />
          <AntdTable.Summary.Cell index={6} align="right">
            {formatEur(report.totalRefundVatEur)}
          </AntdTable.Summary.Cell>
          <AntdTable.Summary.Cell index={7} align="right">
            <Text strong>{formatEur(report.totalNetVatEur)}</Text>
          </AntdTable.Summary.Cell>
        </AntdTable.Summary.Row>
      ) : null,
    [report],
  );

  const delta: number | null =
    report && previousReport
      ? report.totalNetVatEur - previousReport.totalNetVatEur
      : null;

  return (
    <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
      <DetailPageHeader title="OSS VAT report" />

      <Space size={SPACING.md} wrap align="center">
        <DateRangeFilter
          value={range}
          onChange={setRange}
          presets={OSS_DATE_RANGE_PRESETS}
          disabled={Report.inProcess}
        />

        <Button type="primary" onClick={handleLoad} loading={Report.inProcess}>
          <ReloadOutlined /> Load
        </Button>

        <Button disabled={!report} onClick={exportCsv}>
          <DownloadOutlined /> Export CSV
        </Button>

        {renderUpdatedAtMessage(
          Report.updatedAt,
          Report.inProcess,
          (): boolean => Report.oss !== undefined,
        )}
      </Space>

      {report && loadedPeriod && (
        <>
          {report.missingRates.length > 0 && (
            <Alert
              type="warning"
              showIcon
              title={`Missing exchange rate for ${report.missingRates.join(', ')}. Orders in these currencies are not included.`}
            />
          )}

          <Space size={SPACING.xxl} wrap align="start">
            <Space orientation="vertical" size={SPACING.xs}>
              <Statistic
                title={`OSS VAT to pay · ${formatDateRange(loadedPeriod)}`}
                value={report.totalNetVatEur}
                precision={2}
                suffix={EUR_SUFFIX}
              />
              <Text type="secondary">
                Filing and payment due{' '}
                {getOssFilingDeadline(loadedPeriod).format(EDateFormat.DATE)}
              </Text>
            </Space>

            <Space orientation="vertical" size={SPACING.xs}>
              <Statistic
                title="Tax base"
                value={report.totalBaseEur}
                precision={2}
                suffix={EUR_SUFFIX}
              />
            </Space>

            {forecast !== null && (
              <Space orientation="vertical" size={SPACING.xs}>
                <Statistic
                  title={`Forecast for ${getQuarterLabel(loadedPeriod.from)}`}
                  value={forecast}
                  precision={2}
                  suffix={EUR_SUFFIX}
                />
                <Text type="secondary">Linear projection of VAT so far</Text>
              </Space>
            )}

            {previousReport && previousPeriod && delta !== null && (
              <Space orientation="vertical" size={SPACING.xs}>
                <Statistic
                  title={`Previous period · ${formatDateRange(previousPeriod)}`}
                  value={previousReport.totalNetVatEur}
                  precision={2}
                  suffix={EUR_SUFFIX}
                />
                <Text style={{ color: getDeltaColor(delta) }}>
                  {delta >= 0 ? '+' : ''}
                  {formatEur(delta)}
                </Text>
              </Space>
            )}
          </Space>

          {Object.keys(report.exchangeRates).length > 0 && (
            <Text type="secondary">
              Exchange rates (per 1 EUR):{' '}
              {Object.entries(report.exchangeRates)
                .map(
                  ([isoCode, rate]: [string, number]): string =>
                    `${isoCode} ${rate}`,
                )
                .join(', ')}
            </Text>
          )}

          <Table
            columns={
              columns as (ColumnGroupType<object> | ColumnType<object>)[]
            }
            dataSource={dataSource}
            isLoading={Report.inProcess}
            emptyText="No OSS sales in this period"
            summary={renderSummary}
          />

          <Text type="secondary">
            Includes paid B2C orders and company orders without VAT ID taxed in
            another EU country than CZ, by payment date. Exchange rates come
            from the shop rate history, not the official ECB rate. Fakturoid
            credit notes are not included; refunds come from withdrawals.
          </Text>
        </>
      )}
    </Space>
  );
}

export default ReportOssContainer;
